import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const positionalEncodingComponent: ComponentDef = {
  id: 'positional-encoding',
  name: 'Positional Encoding',
  description: 'Add position information to token embeddings: RoPE rotates Q/K, ALiBi adds bias to attention scores, Sinusoidal adds a fixed table, Learned trains position embeddings, YaRN extends context via NTK-aware scaling.',
  category: 'component',
  params: [
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'rope',
      options: [
        { label: 'RoPE (LLaMA / GPT-NeoX)', value: 'rope' },
        { label: 'ALiBi (BLOOM)', value: 'alibi' },
        { label: 'Sinusoidal (original Transformer)', value: 'sinusoidal' },
        { label: 'Learned (BERT / GPT-2)', value: 'learned' },
        { label: 'YaRN (context extension)', value: 'yarn' }
      ]
    }
  ],
  buildGraph: (params) => {
    const seq_len = Number(params.seq_len);
    const d_model = Number(params.d_model);
    const variant = String(params.variant || 'rope');

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'rope') {
      // RoPE: split Q and K into pairs, rotate each pair by theta_i = pos / 10000^(2i/d)
      g.reshape('pairs', 'x', [seq_len, d_model / 2, 2]);
      g.activation('angles', 'pairs', 'theta = pos * 10000^(-2i/d)');
      g.activation('cos_sin', 'angles', 'cos/sin');
      g.mul('rotated', 'pairs', 'cos_sin');
      g.reshape('out_rope', 'rotated', [seq_len, d_model]);
      g.output('out', 'out_rope');

    } else if (variant === 'alibi') {
      // ALiBi: subtract m*|i-j| from QK scores
      g.activation('slopes', 'x', 'head slopes m_h = 2^(-8h/H)');
      g.reshape('pos_diff', 'slopes', [seq_len, seq_len]);
      g.mul('alibi_bias', 'slopes', 'pos_diff');
      g.add('qk_biased', 'x', 'alibi_bias');
      g.output('out', 'qk_biased');

    } else if (variant === 'sinusoidal') {
      // Sinusoidal: PE[pos, 2i] = sin(pos/10000^(2i/d)), PE[pos, 2i+1] = cos(...)
      g.activation('pe_table', 'x', 'PE[pos,i] = sin/cos(pos * omega_i)');
      g.add('out_sin', 'x', 'pe_table');
      g.output('out', 'out_sin');

    } else if (variant === 'yarn') {
      // YaRN: combines NTK-aware interpolation + dynamic NTK scaling + temperature
      // 1. Compute NTK-aware base: scale wavelength base by (s/s_0)^(d/(d-2))
      g.activation('ntk_base', 'x', 'NTK-aware base: b = b_0 * (s/s_0)^(d/(d-2))');
      // 2. Per-dimension dynamic NTK scaling factor r_i
      g.activation('dynamic_scale', 'ntk_base', 'dynamic NTK r_i per dim');
      // 3. Temperature scaling t for attention sharpness
      g.scale('temp_scaled', 'dynamic_scale', 'temperature t');
      // 4. Apply scaled RoPE-style rotation with adjusted frequencies
      g.reshape('pairs', 'temp_scaled', [seq_len, d_model / 2, 2]);
      g.activation('yarn_angles', 'pairs', 'YaRN angles: pos / (b * r_i)^{2i/d} / t');
      g.activation('cos_sin', 'yarn_angles', 'cos/sin');
      g.mul('rotated', 'pairs', 'cos_sin');
      g.reshape('out_yarn', 'rotated', [seq_len, d_model]);
      g.output('out', 'out_yarn');

    } else {
      // Learned: trainable embedding table indexed by position ids [0..seq_len)
      g.input('pos_ids', [seq_len]);
      g.embedding('pos_embed', 'pos_ids', [seq_len, d_model]);
      g.add('out_learned', 'x', 'pos_embed');
      g.output('out', 'out_learned');
    }

    return g.build();
  }
};
